import React, { useEffect, useState, useCallback } from 'react';
import { FB_APP_ID, FB_APP_VERSION } from '../constants/config';
import * as R from 'ramda';
import FacebookManager from './FacebookManager';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import settingsActions from '../actions/settings';
import InstagramManager from './InstagramManager';
import { fbSdkURL } from '../utils/apiUrls';

const FacebookAndInstagramManager = ({ extendToken }) => {
    const { t } = useTranslation('settings');
    const [modal, setModal] = useState(null);

    const initializeScript = useCallback((d, s, id) => {
        let js,
            fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = '//connect.facebook.net/en_US/sdk.js';
        fjs.parentNode.insertBefore(js, fjs);
    }, []);

    useEffect(() => {
        //Initialize both FB and IG
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: FB_APP_ID,
                cookie: true,
                xfbml: false,
                version: FB_APP_VERSION,
                status: true, // set this status to true, this will fixed popup blocker issue
            });
        };

        initializeScript(document, 'script', 'facebook-jssdk');
    }, [initializeScript]);

    const _validateScope = useCallback(
        async (expectedScopes, token, grantedScopes = '') => {
            let missingScopes = [];
            const grantedScopesArray = grantedScopes.split(',');
            const expectedScopesArray = expectedScopes.split(',');
            if (grantedScopes.length > 0) {
                expectedScopesArray.forEach(expectedScope => {
                    if (!grantedScopesArray.includes(expectedScope)) {
                        missingScopes.push(expectedScope);
                    }
                });
            } else {
                //Validate scopes returned
                const response = await fetch(
                    `${fbSdkURL}/me/permissions?access_token=${token}`,
                );
                const data = await response.json();
                const scopes = data.data.reduce((acc, permission) => {
                    if (permission.status === 'granted') {
                        acc.push(permission.permission);
                    }
                    return acc;
                }, []);
                expectedScopesArray.forEach(expectedScope => {
                    if (!scopes.includes(expectedScope)) {
                        missingScopes.push(expectedScope);
                    }
                });
            }

            return [missingScopes.length === 0, missingScopes];
        },
        [],
    );

    const _asyncLogin = useCallback(async scopes => {
        return new Promise(resolve => {
            window.FB.login(response => resolve(response), {
                scope: scopes,
                return_scopes: true,
                enable_profile_selector: true,
            });
        });
    }, []);

    const _triggerLoginPopUp = useCallback(
        async (scopes, onSuccess, onFailure, type = 'facebook') => {
            const response = await _asyncLogin(scopes);
            const token =
                R.path(['authResponse', 'accessToken'], response) || null;
            if (token === null) {
                onFailure(response);
            } else {
                const grantedScopes =
                    R.path(['authResponse', 'grantedScopes'], response) || '';
                const [scopesAreValid, missingScopes] = await _validateScope(
                    scopes,
                    token,
                    grantedScopes,
                );
                if (scopesAreValid) {
                    await extendToken(token, type);
                    // Pass the token directly to avoid race condition with Redux state
                    onSuccess(token);
                } else {
                    alert(
                        t('clientConfiguration.social.notifications.missingPermissions', {
                            missingScopes: missingScopes.join('\n'),
                        }),
                    );
                }
            }
        },
        [_asyncLogin, _validateScope, extendToken, t],
    );

    const _loginNew = useCallback(
        async (scopes, onSuccess, onFailure, type = 'facebook') => {
            _triggerLoginPopUp(scopes, onSuccess, onFailure, type);
        },
        [_triggerLoginPopUp],
    );

    const _onCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    const _switchModal = useCallback(modal => {
        setModal(modal);
    }, []);

    return (
        <React.Fragment>
            <FacebookManager
                login={_loginNew}
                modal={modal}
                triggerModalChange={_switchModal}
                onCloseModal={_onCloseModal}
            />
            <InstagramManager
                login={_loginNew}
                modal={modal}
                triggerModalChange={_switchModal}
                onCloseModal={_onCloseModal}
            />
        </React.Fragment>
    );
};

FacebookAndInstagramManager.propTypes = {
    extendToken: PropTypes.func.isRequired,
};

export default connect(
    ['user', 'instagram', 'facebook'],
    settingsActions,
)(FacebookAndInstagramManager);
